import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Skeleton,
  Alert,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp as TrendingIcon,
  People as PeopleIcon,
  Star as StarIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useDebounce } from '../hooks/useDebounce';
import UserProfileCard from '../components/Social/UserProfileCard';
import { FollowUserProfile } from '../types/follow';
import { UserService } from '../services/user-service';
import discoveryService, { PopularUser, TrendingUser, UserRecommendation } from '../services/discovery-service';
import { logger } from '../utils/logger';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`discover-tabpanel-${index}`}
      aria-labelledby={`discover-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `discover-tab-${index}`,
    'aria-controls': `discover-tabpanel-${index}`,
  };
}

const DiscoverPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<FollowUserProfile[]>([]);
  const [popularUsers, setPopularUsers] = useState<PopularUser[]>([]);
  const [trendingUsers, setTrendingUsers] = useState<TrendingUser[]>([]);
  const [recommendations, setRecommendations] = useState<UserRecommendation[]>([]);
  
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingPopular, setIsLoadingPopular] = useState(false);
  const [isLoadingTrending, setIsLoadingTrending] = useState(false);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
  
  const [error, setError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setError(null);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setSearchError(null);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchError(null);
  };

  // Search users
  const searchUsers = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setSearchError(null);

    try {
      const userService = new UserService();
      const result = await userService.searchUsers(query, {
        limit: 20,
        sortBy: 'relevance',
      });

      // Convert UserProfile[] to FollowUserProfile[]
      const followUserProfiles: FollowUserProfile[] = result.users.map(user => ({
        id: user.id,
        username: user.displayName, // Use displayName as username
        displayName: user.displayName,
        profilePicture: user.profilePicture,
        followerCount: user.followerCount || 0, // Use real follower count from backend
        followingCount: user.followingCount || 0, // Use real following count from backend
        isVerified: user.isVerified || false,
        bio: '',
        isPrivate: user.isPrivate || false,
      }));
      setSearchResults(followUserProfiles);
      logger.info('Search completed', { query, resultCount: result.users.length });
    } catch (error) {
      logger.error('Search failed:', error);
      setSearchError('Failed to search users. Please try again.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Load popular users
  const loadPopularUsers = useCallback(async () => {
    setIsLoadingPopular(true);
    setError(null);

    try {
      const result = await discoveryService.getPopularUsers({
        limit: 20,
        minFollowers: 1, // Minimum followers for popular users (changed from 100 to show users in development)
        includeRecentActivity: true,
      });

      console.log('🔍 Popular Users API Response:', JSON.stringify(result.users.slice(0, 2), null, 2));
      setPopularUsers(result.users);
      logger.info('Popular users loaded', { count: result.users.length });
    } catch (error) {
      logger.error('Failed to load popular users:', error);
      setError('Failed to load popular users. Please try again.');
    } finally {
      setIsLoadingPopular(false);
    }
  }, []);

  // Load trending users
  const loadTrendingUsers = useCallback(async () => {
    setIsLoadingTrending(true);
    setError(null);

    try {
      const users = await discoveryService.getTrendingUsers(20);
      console.log('🔍 Trending Users API Response:', JSON.stringify(users.slice(0, 2), null, 2));
      // Convert User[] to TrendingUser[] by adding trending properties
      const trendingUsers: TrendingUser[] = users.map(user => ({
        ...user,
        trendingScore: 100, // Default trending score
        trendingReason: 'Popular creator'
      }));
      setTrendingUsers(trendingUsers);
      logger.info('Trending users loaded', { count: trendingUsers.length });
    } catch (error) {
      logger.error('Failed to load trending users:', error);
      setError('Failed to load trending users. Please try again.');
    } finally {
      setIsLoadingTrending(false);
    }
  }, []);

  // Load recommendations
  const loadRecommendations = useCallback(async () => {
    setIsLoadingRecommendations(true);
    setError(null);

    try {
      const result = await discoveryService.getUserRecommendations({
        limit: 15,
        category: 'for_you',
        includeMetrics: true,
      });

      console.log('🔍 Recommendations API Response:', JSON.stringify(result.recommendations.slice(0, 2), null, 2));
      setRecommendations(result.recommendations);
      logger.info('For You recommendations loaded', { 
        count: result.recommendations.length,
        personalized: result.personalized,
        algorithm: result.algorithm
      });
    } catch (error) {
      logger.error('Failed to load recommendations:', error);
      setError('Failed to load recommendations. Please try again.');
    } finally {
      setIsLoadingRecommendations(false);
    }
  }, []);

  // Effect for search
  useEffect(() => {
    if (debouncedSearchQuery) {
      searchUsers(debouncedSearchQuery);
    } else {
      setSearchResults([]);
    }
  }, [debouncedSearchQuery, searchUsers]);

  // Effect for loading data based on active tab
  useEffect(() => {
    switch (activeTab) {
      case 0: // Popular
        if (popularUsers.length === 0) {
          loadPopularUsers();
        }
        break;
      case 1: // Trending
        if (trendingUsers.length === 0) {
          loadTrendingUsers();
        }
        break;
      case 2: // For You
        if (recommendations.length === 0) {
          loadRecommendations();
        }
        break;
    }
  }, [activeTab, popularUsers.length, trendingUsers.length, recommendations.length, loadPopularUsers, loadTrendingUsers, loadRecommendations]);

  // Render user grid
  const renderUserGrid = (users: any[], isLoading: boolean) => {
    if (isLoading) {
      return (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <UserProfileCard
                user={{} as FollowUserProfile}
                isLoading={true}
                showFollowButton={true}
                showStats={true}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (users.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No users found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or check back later for new users.
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {users.map((user) => {
          console.log('🎯 Rendering user card:', { 
            id: user.id, 
            displayName: user.displayName,
            followerCount: user.followerCount,
            followingCount: user.followingCount 
          });
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
              <UserProfileCard
                user={{
                  id: user.id,
                  username: user.username || user.displayName,
                  displayName: user.displayName,
                  profilePicture: user.profilePicture,
                  isVerified: user.isVerified || false,
                  followerCount: user.followerCount || 0,
                  followingCount: user.followingCount || 0,
                  isPrivate: user.isPrivate || false,
                }}
                showFollowButton={true}
                showStats={true}
                onClick={(userId) => {
                  // Navigate to user profile
                  window.location.href = `/profile/${userId}`;
                }}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  // Render trending users with metrics
  const renderTrendingUsers = () => {
    if (isLoadingTrending) {
      return renderUserGrid([], true);
    }

    if (trendingUsers.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          <TrendingIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No trending users found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Check back later to see who's trending in the fashion community.
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {trendingUsers.map((user) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
            <Card variant="outlined" sx={{ height: '100%', backgroundColor: "#E3F2FD", color: "#1C1C1C" }}>
              <CardContent>
                <UserProfileCard
                  user={{
                    id: user.id,
                    username: user.username || user.displayName,
                    displayName: user.displayName,
                    profilePicture: user.profilePicture,
                    isVerified: user.isVerified || false,
                    followerCount: user.followerCount || 0,
                    followingCount: user.followingCount || 0,
                    isPrivate: user.isPrivate || false
                  }}
                  showFollowButton={true}
                  showStats={true}
                  variant="outlined"
                  onClick={(userId) => {
                    window.location.href = `/profile/${userId}`;
                  }}
                />
                
                {user.trendingScore && (
                  <Box mt={2}>
                    <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                      Trending Score: {user.trendingScore}
                    </Typography>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {user.trendingReason && (
                        <Chip
                          label={user.trendingReason}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {user.followerCount && (
                        <Chip
                          label={`${user.followerCount} followers`}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Render recommendations with categories
  const renderRecommendations = () => {
    if (isLoadingRecommendations) {
      return renderUserGrid([], true);
    }

    if (recommendations.length === 0) {
      return (
        <Box textAlign="center" py={4}>
          <StarIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No recommendations available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Follow more users to get personalized recommendations.
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {recommendations.map((user) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
            <Card variant="outlined" sx={{ height: '100%', backgroundColor: "#E3F2FD", color: "#1C1C1C" }}>
              <CardContent>
                <UserProfileCard
                  user={{
                    id: user.id,
                    username: user.username || user.displayName,
                    displayName: user.displayName,
                    profilePicture: user.profilePicture,
                    isVerified: user.isVerified || false,
                    followerCount: user.followerCount || 0,
                    followingCount: user.followingCount || 0,
                    isPrivate: user.isPrivate || false,
                  }}
                  showFollowButton={true}
                  showStats={true}
                  variant="outlined"
                  onClick={(userId) => {
                    window.location.href = `/profile/${userId}`;
                  }}
                />
                
                <Box mt={2}>
                  <Chip
                    label={user.category.replace('_', ' ')}
                    size="small"
                    color="primary"
                    sx={{ textTransform: 'capitalize' }}
                  />
                  {user.connectionInfo && user.connectionInfo.mutualFollows > 0 && (
                    <Chip
                      label={`${user.connectionInfo.mutualFollows} mutual`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ ml: 1 }}
                    />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <Box sx={{ py: 3, width: '100%' }}>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Discover Users
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Find new fashion creators to follow and get inspired by their style.
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box mb={3}>
        <TextField
          fullWidth
          placeholder="Search users by username or name..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Search Results */}
        {searchQuery && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Search Results for "{searchQuery}"
            </Typography>
            
            {searchError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {searchError}
              </Alert>
            )}

            {renderUserGrid(searchResults, isSearching)}
          </Box>
        )}
      </Box>

      {/* Tabs - only show when not searching */}
      {!searchQuery && (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="discover tabs">
              <Tab 
                label="Popular" 
                icon={<PeopleIcon />} 
                iconPosition="start"
                {...a11yProps(0)} 
              />
              <Tab 
                label="Trending" 
                icon={<TrendingIcon />} 
                iconPosition="start"
                {...a11yProps(1)} 
              />
              <Tab 
                label="For You" 
                icon={<StarIcon />} 
                iconPosition="start"
                {...a11yProps(2)} 
              />
            </Tabs>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              action={
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setError(null);
                    // Retry loading based on active tab
                    switch (activeTab) {
                      case 0:
                        loadPopularUsers();
                        break;
                      case 1:
                        loadTrendingUsers();
                        break;
                      case 2:
                        loadRecommendations();
                        break;
                    }
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}

          {/* Tab Panels */}
          <TabPanel value={activeTab} index={0}>
            <Box mb={2}>
              <Typography variant="h6" gutterBottom>
                Popular Users
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Users with the most followers and engagement in the fashion community.
              </Typography>
            </Box>
            {renderUserGrid(popularUsers, isLoadingPopular)}
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Box mb={2}>
              <Typography variant="h6" gutterBottom>
                Trending Creators
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Users who are gaining followers and creating buzz in the fashion world.
              </Typography>
            </Box>
            {renderTrendingUsers()}
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <Box mb={2}>
              <Typography variant="h6" gutterBottom>
                For You
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {recommendations.length > 0 && (recommendations as any)[0]?.algorithm?.includes('personalized') 
                  ? 'Personalized recommendations based on your liked outfits and style preferences.'
                  : 'Discover trending creators and popular users in the fashion community.'}
              </Typography>
              {recommendations.length > 0 && (recommendations as any)[0]?.algorithm && (
                <Chip 
                  label={(recommendations as any)[0].algorithm.includes('personalized') ? 'Personalized' : 'General'}
                  size="small"
                  color={(recommendations as any)[0].algorithm.includes('personalized') ? 'primary' : 'default'}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
            {renderRecommendations()}
          </TabPanel>
        </>
      )}
    </Box>
  );
};

export default DiscoverPage;